/**
 * InspectionFormPage Component
 * Allows users to perform NFPA 10 / OSHA 1910.157 compliant fire extinguisher inspections
 */

import { todayLocal } from '../lib/dates.js';
import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { router } from '../lib/router.js';
import { Toast } from './shared/Toast.js';

interface InspectionFindings {
  // Location & Accessibility
  correct_type_for_hazard: boolean;
  in_designated_location: boolean;
  accessible_and_visible: boolean;
  signage_clear: boolean;
  properly_mounted: boolean;

  // Physical Condition
  no_visible_damage: boolean;
  no_corrosion: boolean;
  no_leakage: boolean;
  hose_nozzle_clear: boolean;

  // Pressure & Charge
  pressure_gauge_status: 'good' | 'low' | 'high' | 'unreadable';
  adequate_weight: boolean;

  // Tamper Indicators
  safety_pin_intact: boolean;
  tamper_seal_unbroken: boolean;

  // Labels & Instructions
  instructions_legible: boolean;
  inspection_tag_present: boolean;
  service_tag_current: boolean;
}

export class InspectionFormPage extends BaseComponent {
  private extinguisherId: string = '';
  private extinguisherData: any = null;
  private selectedFile: File | null = null;
  private photoPreview: string | null = null;
  private isSubmitting: boolean = false;

  async connectedCallback() {
    super.connectedCallback();

    // Get extinguisher ID from route params
    const pathParts = window.location.pathname.split('/');
    this.extinguisherId = pathParts[pathParts.length - 2]; // /extinguishers/:id/inspect

    await this.loadExtinguisher();
    await this.render();
  }

  private async loadExtinguisher(): Promise<void> {
    try {
      const response = await api.get(`/api/extinguishers/${this.extinguisherId}`);
      this.extinguisherData = response;
    } catch (error) {
      console.error('Failed to load extinguisher:', error);
      this.showToast('Failed to load extinguisher details', 'error');
      router.push('/extinguishers');
    }
  }

  protected async render(): Promise<void> {
    if (!this.extinguisherData) {
      this.shadow.innerHTML = '<loading-spinner></loading-spinner>';
      return;
    }

    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'page-container' });

    // Header
    const header = this.createElement('div', { className: 'page-header' });
    const backButton = this.createElement('button', {
      className: 'back-button',
      textContent: '← Back'
    });
    backButton.addEventListener('click', () => {
      router.push(`/extinguishers/${this.extinguisherId}`);
    });

    const title = this.createElement('h1', {
      textContent: 'Fire Extinguisher Inspection'
    });

    // The column is extinguisher_type; there is no `type` column, and reading
    // one printed "undefined" above every checklist. Lead with the agent where
    // we know it, because that is what the inspector is holding.
    const cls = this.extinguisherData.extinguisher_type || '';
    const agent = this.extinguisherData.agent || '';
    const descriptor = [agent, cls && (agent ? `(class ${cls})` : `Class ${cls}`)].filter(Boolean).join(' ');
    const identifier = this.extinguisherData.qr_code || `ID: ${String(this.extinguisherData.id || '').substr(0, 8)}`;
    const subtitle = this.createElement('p', {
      className: 'subtitle',
      textContent: descriptor ? `${descriptor} \u00b7 ${identifier}` : identifier
    });

    header.appendChild(backButton);
    header.appendChild(title);
    header.appendChild(subtitle);

    // Form
    const form = this.createInspectionForm();

    container.appendChild(header);
    container.appendChild(form);

    this.shadow.appendChild(container);
  }

  private createInspectionForm(): HTMLElement {
    const form = this.createElement('form', { className: 'inspection-form' });
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Inspection Type
    const typeSection = this.createSection('Record type', [
      this.createRadioGroup('inspection_type', [
        { label: 'Monthly visual inspection (your staff, NFPA 10 §7.2)', value: 'monthly', checked: true },
        { label: 'Annual maintenance (certified technician, NFPA 10 §7.3)', value: 'annual' },
        { label: 'Six-year internal examination (NFPA 10 §7.3.3)', value: 'six_year' },
        { label: 'Hydrostatic test (NFPA 10 Chapter 8)', value: 'hydrostatic' }
      ])
    ]);

    // Service record fields (annual / six-year / hydrostatic)
    const serviceSection = this.createSection('Service record', [
      this.createInput('service_company', 'Service company', 'e.g. Cintas, Koorsen, local licensed contractor'),
      this.createInput('technician', 'Technician / certification no.', 'Name and license or certification number'),
      this.createInput('next_due', 'Next due date (from the service tag or collar)', '', 'date')
    ]);
    serviceSection.dataset.for = 'annual six_year hydrostatic';

    // Section 1: Location & Accessibility
    const locationSection = this.createSection('1. Location & Accessibility', [
      this.createCheckbox('in_designated_location', 'Extinguisher in designated location'),
      this.createCheckbox('correct_type_for_hazard', 'Correct class for the hazard it protects (e.g. K-class still at the cooking line)'),
      this.createCheckbox('accessible_and_visible', 'Accessible and visible (no obstructions)'),
      this.createCheckbox('signage_clear', 'Signage visible and clear'),
      this.createCheckbox('properly_mounted', 'Properly mounted on bracket/cabinet')
    ]);

    // Section 2: Physical Condition
    const physicalSection = this.createSection('2. Physical Condition', [
      this.createCheckbox('no_visible_damage', 'No visible damage (dents, cracks, punctures)'),
      this.createCheckbox('no_corrosion', 'No rust or corrosion'),
      this.createCheckbox('no_leakage', 'No signs of agent leaking'),
      this.createCheckbox('hose_nozzle_clear', 'Hose/nozzle clear and properly attached')
    ]);

    // Section 3: Pressure & Charge
    //
    // A CO2 cylinder has no gauge; its charge is verified by weight. Where the
    // record knows the agent, the form should not open by asking the inspector
    // to read a gauge that is not there — and weight stops being a secondary
    // heft check and becomes the only evidence of charge there is.
    const gaugeless = String(this.extinguisherData.agent || '').toUpperCase() === 'CO2';
    const pressureItems: HTMLElement[] = [
      this.createRadioGroup('pressure_gauge_status', [
        { label: '✅ Green Zone (Good)', value: 'good', checked: !gaugeless },
        { label: '⚠️ Red Low (Undercharged)', value: 'low' },
        { label: '⚠️ Red High (Overcharged)', value: 'high' },
        { label: '❌ Unreadable', value: 'unreadable' },
        { label: 'No gauge on this unit (CO2 / cartridge) - checked by weight', value: 'no_gauge', checked: gaugeless }
      ]),
      this.createCheckbox(
        'adequate_weight',
        gaugeless
          ? 'Weight verified against the nameplate (a CO2 unit is charged by weight, not by a gauge)'
          : 'Adequate weight (lift test passed)'
      )
    ];
    if (gaugeless) {
      pressureItems.unshift(this.createElement('p', {
        className: 'field-note',
        textContent: 'This unit is recorded as CO2, so it has no gauge. Verify the charge by weight; NFPA 10 calls for recharge when it is more than 10% under the nameplate weight.'
      }));
    }
    const pressureSection = this.createSection('3. Pressure & Charge', pressureItems);

    // Section 4: Tamper Indicators
    const tamperSection = this.createSection('4. Tamper Indicators', [
      this.createCheckbox('safety_pin_intact', 'Safety pin intact and secured'),
      this.createCheckbox('tamper_seal_unbroken', 'Tamper seal unbroken')
    ]);

    // Section 5: Labels & Instructions
    const labelsSection = this.createSection('5. Labels & Instructions', [
      this.createCheckbox('instructions_legible', 'Operating instructions legible and facing outward'),
      this.createCheckbox('inspection_tag_present', 'Last monthly inspection recorded (tag or electronic record)'),
      this.createCheckbox('service_tag_current', 'Service tag present and current (within 12 months)')
    ]);

    // Photo Upload
    const photoSection = this.createSection('Photo Documentation (Optional)', [
      this.createPhotoUpload()
    ]);

    // Notes
    const notesSection = this.createSection('Additional Notes', [
      this.createTextArea('notes', 'Enter any additional observations or concerns...')
    ]);

    // Overall Result
    const resultSection = this.createSection('Inspection Result', [
      this.createRadioGroup('status', [
        { label: '✅ PASS - All items satisfactory', value: 'pass', checked: true },
        { label: '⚠️ WARNING - Minor issues, monitor closely', value: 'warning' },
        { label: '❌ FAIL - Critical issues, remove from service', value: 'fail' }
      ])
    ]);

    // Submit Button
    const submitButton = this.createElement('button', {
      type: 'submit',
      className: 'submit-button',
      textContent: this.isSubmitting ? 'Submitting...' : 'Submit Inspection'
    }) as HTMLButtonElement;
    submitButton.disabled = this.isSubmitting;

    for (const sec of [locationSection, physicalSection, pressureSection, tamperSection, labelsSection]) sec.dataset.for = 'monthly annual';

    form.appendChild(typeSection);
    form.appendChild(serviceSection);
    form.appendChild(locationSection);
    form.appendChild(physicalSection);
    form.appendChild(pressureSection);
    form.appendChild(tamperSection);
    form.appendChild(labelsSection);
    form.appendChild(photoSection);
    form.appendChild(notesSection);
    form.appendChild(resultSection);
    form.appendChild(submitButton);

    const applyType = () => {
      const type = (form.querySelector('[name="inspection_type"]:checked') as HTMLInputElement)?.value || 'monthly';
      form.querySelectorAll<HTMLElement>('[data-for]').forEach((el) => {
        el.hidden = !(el.dataset.for || '').split(' ').includes(type);
      });
    };
    form.querySelectorAll('[name="inspection_type"]').forEach((r) => r.addEventListener('change', applyType));
    applyType();

    return form;
  }

  private createSection(title: string, fields: HTMLElement[]): HTMLElement {
    const section = this.createElement('div', { className: 'form-section' });
    const sectionTitle = this.createElement('h3', { textContent: title });
    section.appendChild(sectionTitle);

    fields.forEach(field => section.appendChild(field));

    return section;
  }

  private createCheckbox(name: string, label: string): HTMLElement {
    const wrapper = this.createElement('div', { className: 'checkbox-field' });

    const checkbox = this.createElement('input', {
      type: 'checkbox',
      name: name,
      id: name,
      checked: true
    }) as HTMLInputElement;

    const labelEl = this.createElement('label', {
      htmlFor: name,
      textContent: label
    });

    wrapper.appendChild(checkbox);
    wrapper.appendChild(labelEl);

    return wrapper;
  }

  private createRadioGroup(name: string, options: Array<{label: string, value: string, checked?: boolean}>): HTMLElement {
    const wrapper = this.createElement('div', { className: 'radio-group' });

    options.forEach(option => {
      const radioWrapper = this.createElement('div', { className: 'radio-field' });

      const radio = this.createElement('input', {
        type: 'radio',
        name: name,
        value: option.value,
        id: `${name}_${option.value}`,
        checked: option.checked || false
      }) as HTMLInputElement;

      const label = this.createElement('label', {
        htmlFor: `${name}_${option.value}`,
        textContent: option.label
      });

      radioWrapper.appendChild(radio);
      radioWrapper.appendChild(label);
      wrapper.appendChild(radioWrapper);
    });

    return wrapper;
  }

  private createInput(name: string, label: string, placeholder: string, type: 'text' | 'date' = 'text'): HTMLElement {
    const wrapper = this.createElement('div', { className: 'input-field' });
    const labelEl = this.createElement('label', { htmlFor: `field_${name}`, textContent: label });
    const input = this.createElement('input', { type, name, id: `field_${name}`, placeholder, className: 'text-input' }) as HTMLInputElement;
    if (type === 'date') input.max = new Date(Date.now() + 20 * 365 * 86400000).toISOString().slice(0, 10);
    wrapper.appendChild(labelEl);
    wrapper.appendChild(input);
    return wrapper;
  }

  private createTextArea(name: string, placeholder: string): HTMLElement {
    const textarea = this.createElement('textarea', {
      name: name,
      id: name,
      placeholder: placeholder,
      rows: '4'
    }) as HTMLTextAreaElement;

    return textarea;
  }

  private createPhotoUpload(): HTMLElement {
    const wrapper = this.createElement('div', { className: 'photo-upload' });

    const fileInput = this.createElement('input', {
      type: 'file',
      accept: 'image/*',
      id: 'photo-input',
      style: 'display: none;'
    }) as HTMLInputElement;

    fileInput.addEventListener('change', (e) => this.handlePhotoSelect(e));

    const uploadButton = this.createElement('button', {
      type: 'button',
      className: 'upload-button',
      textContent: '📷 Take/Upload Photo'
    });

    uploadButton.addEventListener('click', () => fileInput.click());

    const preview = this.createElement('div', {
      className: 'photo-preview',
      id: 'photo-preview',
      style: 'display: none;'
    });

    const helperText = this.createElement('p', {
      className: 'helper-text',
      textContent: 'Recommended: Capture pressure gauge, tamper seal, and overall condition'
    });

    wrapper.appendChild(fileInput);
    wrapper.appendChild(uploadButton);
    wrapper.appendChild(preview);
    wrapper.appendChild(helperText);

    return wrapper;
  }

  private handlePhotoSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.selectedFile = input.files[0];

      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.photoPreview = e.target?.result as string;
        const preview = this.shadow.getElementById('photo-preview');
        if (preview) {
          preview.style.display = 'block';
          preview.innerHTML = `
            <img src="${this.photoPreview}" alt="Inspection photo" style="max-width: 100%; border-radius: 8px;" />
            <button type="button" class="remove-photo" style="margin-top: 8px;">Remove Photo</button>
          `;

          const removeBtn = preview.querySelector('.remove-photo');
          removeBtn?.addEventListener('click', () => this.removePhoto());
        }
      };
      reader.readAsDataURL(this.selectedFile);
    }
  }

  private removePhoto(): void {
    this.selectedFile = null;
    this.photoPreview = null;
    const preview = this.shadow.getElementById('photo-preview');
    if (preview) {
      preview.style.display = 'none';
      preview.innerHTML = '';
    }
    const input = this.shadow.getElementById('photo-input') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.updateSubmitButton();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);

    // Collect findings from checkboxes
    const findings: InspectionFindings = {
      in_designated_location: (form.querySelector('[name="in_designated_location"]') as HTMLInputElement)?.checked || false,
      accessible_and_visible: (form.querySelector('[name="accessible_and_visible"]') as HTMLInputElement)?.checked || false,
      signage_clear: (form.querySelector('[name="signage_clear"]') as HTMLInputElement)?.checked || false,
      properly_mounted: (form.querySelector('[name="properly_mounted"]') as HTMLInputElement)?.checked || false,
      no_visible_damage: (form.querySelector('[name="no_visible_damage"]') as HTMLInputElement)?.checked || false,
      no_corrosion: (form.querySelector('[name="no_corrosion"]') as HTMLInputElement)?.checked || false,
      no_leakage: (form.querySelector('[name="no_leakage"]') as HTMLInputElement)?.checked || false,
      hose_nozzle_clear: (form.querySelector('[name="hose_nozzle_clear"]') as HTMLInputElement)?.checked || false,
      pressure_gauge_status: (form.querySelector('[name="pressure_gauge_status"]:checked') as HTMLInputElement)?.value as any || 'good',
      adequate_weight: (form.querySelector('[name="adequate_weight"]') as HTMLInputElement)?.checked || false,
      safety_pin_intact: (form.querySelector('[name="safety_pin_intact"]') as HTMLInputElement)?.checked || false,
      tamper_seal_unbroken: (form.querySelector('[name="tamper_seal_unbroken"]') as HTMLInputElement)?.checked || false,
      instructions_legible: (form.querySelector('[name="instructions_legible"]') as HTMLInputElement)?.checked || false,
      inspection_tag_present: (form.querySelector('[name="inspection_tag_present"]') as HTMLInputElement)?.checked || false,
      service_tag_current: (form.querySelector('[name="service_tag_current"]') as HTMLInputElement)?.checked || false
    };

    // Unchecked checklist items become the written findings on the record.
    const checklistLabels: Record<string, string> = {
      in_designated_location: 'Not in designated location',
      accessible_and_visible: 'Not accessible or visible',
      signage_clear: 'Signage missing or unclear',
      properly_mounted: 'Not properly mounted',
      no_visible_damage: 'Visible damage',
      no_corrosion: 'Rust or corrosion',
      no_leakage: 'Signs of leakage',
      hose_nozzle_clear: 'Hose or nozzle obstructed or loose',
      adequate_weight: 'Weight low (lift test failed)',
      safety_pin_intact: 'Safety pin missing or unsecured',
      tamper_seal_unbroken: 'Tamper seal broken',
      instructions_legible: 'Operating instructions not legible',
      inspection_tag_present: 'No record of the previous monthly inspection',
      service_tag_current: 'Service tag missing or older than 12 months'
    };
    const recordType = (form.querySelector('[name="inspection_type"]:checked') as HTMLInputElement)?.value || 'monthly';
    const isServiceRecord = recordType === 'six_year' || recordType === 'hydrostatic';
    const failedItems = isServiceRecord
      ? []
      : Object.entries(checklistLabels)
          .filter(([key]) => !(findings as unknown as Record<string, boolean>)[key])
          .map(([, label]) => label);
    if (!isServiceRecord && !['good', 'no_gauge'].includes(findings.pressure_gauge_status)) failedItems.unshift(`Pressure gauge ${findings.pressure_gauge_status}`);
    const notes = (form.querySelector('[name="notes"]') as HTMLTextAreaElement)?.value.trim() || '';
    const serviceCompany = (form.querySelector('[name="service_company"]') as HTMLInputElement)?.value.trim() || '';
    const technician = (form.querySelector('[name="technician"]') as HTMLInputElement)?.value.trim() || '';
    const nextDue = (form.querySelector('[name="next_due"]') as HTMLInputElement)?.value || '';
    const serviceLine = recordType !== 'monthly' && (serviceCompany || technician) ? `Performed by ${[technician, serviceCompany].filter(Boolean).join(', ')}` : '';
    const findingsText = [failedItems.join('; '), serviceLine, notes].filter(Boolean).join(' — ');

    // A "pass" with failed checklist items is not a pass; record it as a warning and say so.
    let verdict = (form.querySelector('[name="status"]:checked') as HTMLInputElement)?.value || 'pass';
    if (verdict === 'pass' && failedItems.length > 0) {
      verdict = 'warning';
      this.showToast('Some checklist items failed, so this is recorded as a warning, not a pass.', 'info');
    }

    // Build inspection payload in the shape POST /api/inspections expects
    const inspectionData = {
      extinguisher_id: this.extinguisherId,
      inspection_type: recordType,
      inspection_date: todayLocal(),
      status: verdict,
      pressure_gauge_status: isServiceRecord ? 'no_gauge' : findings.pressure_gauge_status,
      seal_intact: isServiceRecord ? true : findings.tamper_seal_unbroken && findings.safety_pin_intact,
      findings: findingsText,
      checklist: isServiceRecord ? { service_company: serviceCompany, technician } : { ...findings, service_company: serviceCompany || undefined, technician: technician || undefined },
      next_due: nextDue || undefined,
      notes
    };

    try {
      // If photo is present, upload to R2 and analyze with AI
      let photoData = null;
      if (this.selectedFile) {
        photoData = await this.uploadPhoto(this.selectedFile);
      }

      // Submit inspection
      const response = await api.post('/api/inspections', {
        ...inspectionData,
        photo_url: photoData?.url,
        ai_analysis: photoData?.analysis
      });

      this.showToast('Inspection submitted successfully!', 'success');

      // Navigate back to extinguisher detail
      setTimeout(() => {
        router.push(`/extinguishers/${this.extinguisherId}`);
      }, 1500);

    } catch (error: any) {
      console.error('Failed to submit inspection:', error);
      this.showToast(error.message || 'Failed to submit inspection', 'error');
      this.isSubmitting = false;
      this.updateSubmitButton();
    }
  }

  private async uploadPhoto(file: File): Promise<{ url: string; analysis: any } | null> {
    try {
      // Multipart upload to /api/inspections/photo-analysis (stores in R2, runs Workers AI)
      const result = await api.analyzePhoto(file, this.extinguisherId);
      return { url: result.photo_url, analysis: result.analysis };
    } catch (error) {
      // The photo is evidence, not a gate: record the inspection even if analysis fails.
      console.error('Photo upload/analysis failed:', error);
      this.showToast('Photo could not be analyzed; the inspection will be saved without it.', 'info');
      return null;
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private updateSubmitButton(): void {
    const submitButton = this.shadow.querySelector('.submit-button') as HTMLButtonElement;
    if (submitButton) {
      submitButton.textContent = this.isSubmitting ? 'Submitting...' : 'Submit Inspection';
      submitButton.disabled = this.isSubmitting;
    }
  }

  private showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success'): void {
    // Routes through the registered <app-toast> component; a bare document.createElement
    // of an unregistered tag renders nothing and silently swallows the message.
    if (type === 'error') Toast.error(message);
    else if (type === 'warning') Toast.warning(message);
    else if (type === 'info') Toast.info(message);
    else Toast.success(message);
  }

  private getStyles(): string {
    return `
      .input-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
      .input-field label { font-weight: 600; font-size: 14px; }
      .text-input { padding: 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 16px; width: 100%; box-sizing: border-box; }
      [hidden] { display: none !important; }
      .page-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 24px;
      }

      .page-header {
        margin-bottom: 32px;
      }

      .back-button {
        background: none;
        border: none;
        color: var(--primary-color);
        font-size: 14px;
        cursor: pointer;
        padding: 8px 0;
        margin-bottom: 16px;
      }

      .back-button:hover {
        text-decoration: underline;
      }

      h1 {
        font-size: 28px;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      .subtitle {
        color: var(--text-secondary);
        font-size: 16px;
        margin: 0;
      }

      .inspection-form {
        background: white;
        border-radius: 12px;
        padding: 32px;
        box-shadow: var(--shadow-md);
      }

      .form-section {
        margin-bottom: 32px;
        padding-bottom: 24px;
        border-bottom: 1px solid var(--border-color);
      }

      .form-section:last-of-type {
        border-bottom: none;
      }

      .form-section h3 {
        font-size: 18px;
        color: var(--text-primary);
        margin: 0 0 16px 0;
        font-weight: 600;
      }

      .checkbox-field, .radio-field {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
        padding: 8px;
        border-radius: 6px;
        transition: background-color 0.2s;
      }

      .checkbox-field:hover, .radio-field:hover {
        background-color: var(--bg-secondary);
      }

      .checkbox-field input[type="checkbox"],
      .radio-field input[type="radio"] {
        width: 20px;
        height: 20px;
        cursor: pointer;
      }

      .checkbox-field label, .radio-field label {
        font-size: 15px;
        color: var(--text-primary);
        cursor: pointer;
        flex: 1;
      }

      .radio-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      /* Says why the form is already answered a particular way. */
      .field-note {
        margin: 0 0 12px;
        padding: 10px 12px;
        border-left: 3px solid #b45309;
        background: #fffbeb;
        color: #78350f;
        font-size: 14px;
        line-height: 1.45;
        border-radius: 0 6px 6px 0;
      }

      textarea {
        width: 100%;
        padding: 12px;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        font-family: inherit;
        font-size: 15px;
        resize: vertical;
        min-height: 100px;
      }

      textarea:focus {
        outline: none;
        border-color: var(--primary-color);
      }

      .photo-upload {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .upload-button {
        background: var(--primary-color);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        align-self: flex-start;
      }

      .upload-button:hover {
        background: var(--primary-hover);
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      .photo-preview {
        margin-top: 16px;
      }

      .remove-photo {
        background: var(--danger-color);
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        font-size: 14px;
        cursor: pointer;
      }

      .helper-text {
        font-size: 13px;
        color: var(--text-secondary);
        margin: 0;
        font-style: italic;
      }

      .submit-button {
        width: 100%;
        background: var(--success-color);
        color: white;
        border: none;
        padding: 16px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        margin-top: 24px;
      }

      .submit-button:hover:not(:disabled) {
        background: #059669;
        transform: translateY(-2px);
        box-shadow: var(--shadow-lg);
      }

      .submit-button:disabled {
        background: #9ca3af;
        cursor: not-allowed;
        transform: none;
      }

      @media (max-width: 768px) {
        .page-container {
          padding: 16px;
        }

        .inspection-form {
          padding: 24px 16px;
        }

        h1 {
          font-size: 24px;
        }
      }
    `;
  }
}

customElements.define('inspection-form-page', InspectionFormPage);
